import { useLogoStore } from "../state/logoStore";

/**
 * Apple-style floating dock — pinned top-center on every page.
 *
 * Items: Logo Maker · GIF Maker · QR Maker · Templates · Resources · Lookymart.
 * Lookymart is the only external link (opens https://lookymart.com/ in a new tab).
 *
 * Position is `fixed` so it sits above the page header and stays put while the
 * user scrolls — same behavior as the macOS dock.
 */
export function TopDock() {
  const setPage = useLogoStore((s) => s.setPage);
  const goto = (target: "start" | "templates" | "onboarding" | "gif-maker" | "qr-maker" | "poster-tiler" | "threed") => {
    setPage(target);
    window.scrollTo({ top: 0 });
  };

  return (
    <div className="dock-wrap" aria-label="Quick navigation dock">
      <nav className="dock">
        <DockItem
          label="Home"
          tone="home"
          onClick={() => goto("start")}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 11l9-8 9 8v10a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2V11z"/>
            </svg>
          }
        />
        <DockItem
          label="Logo Maker"
          tone="logo"
          onClick={() => goto("onboarding")}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 19V5h6"/>
              <path d="M5 19h10"/>
            </svg>
          }
        />
        <DockItem
          label="3D Logo"
          tone="threed"
          onClick={() => goto("threed")}
          icon={<span className="dock-text-icon">3D</span>}
        />
        <DockItem
          label="GIF Maker"
          tone="gif"
          onClick={() => goto("gif-maker")}
          icon={<span className="dock-text-icon">GIF</span>}
        />
        <DockItem
          label="QR Maker"
          tone="qr"
          onClick={() => goto("qr-maker")}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/>
              <rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5"/>
              <path d="M14 14h3v3M21 14v3M14 17v4M17 21h4"/>
            </svg>
          }
        />
        <DockItem
          label="Poster Tiler"
          tone="poster"
          onClick={() => goto("poster-tiler")}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2"/>
              <line x1="3" y1="9" x2="21" y2="9"/>
              <line x1="3" y1="15" x2="21" y2="15"/>
              <line x1="9" y1="3" x2="9" y2="21"/>
              <line x1="15" y1="3" x2="15" y2="21"/>
            </svg>
          }
        />
        <DockItem
          label="Templates"
          tone="templates"
          onClick={() => goto("templates")}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3"  width="8" height="8" rx="1.5"/>
              <rect x="13" y="3" width="8" height="8" rx="1.5"/>
              <rect x="3" y="13" width="8" height="8" rx="1.5"/>
              <rect x="13" y="13" width="8" height="8" rx="1.5"/>
            </svg>
          }
        />
        <DockItem
          label="Resources"
          tone="resources"
          onClick={() => goto("templates")}
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
            </svg>
          }
        />
        <span className="dock-separator" aria-hidden="true" />
        <DockItem
          label="Lookymart"
          tone="lookymart"
          href="https://lookymart.com/"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
              <line x1="3" y1="6" x2="21" y2="6"/>
              <path d="M16 10a4 4 0 0 1-8 0"/>
            </svg>
          }
        />
      </nav>
    </div>
  );
}

function DockItem(props: {
  label: string;
  tone: "home" | "logo" | "threed" | "gif" | "qr" | "poster" | "templates" | "resources" | "lookymart";
  icon: React.ReactNode;
  onClick?: () => void;
  href?: string;
}) {
  const inner = (
    <>
      <span className={`dock-icon dock-icon-${props.tone}`}>{props.icon}</span>
      <span className="dock-label">{props.label}</span>
    </>
  );
  if (props.href) {
    const isExternal = /^https?:\/\//i.test(props.href);
    return (
      <a
        href={props.href}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
        className="dock-item"
        data-no-sound
        aria-label={isExternal ? `${props.label} (opens in a new tab)` : props.label}
      >
        {inner}
      </a>
    );
  }
  return (
    <button
      type="button"
      className="dock-item"
      onClick={props.onClick}
      aria-label={props.label}
    >
      {inner}
    </button>
  );
}
